import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';

// =============================================================================
// Types
// =============================================================================

export interface ImportErrorDetail {
  message: string;
  errorType?: string | null;
  timestamp?: string;
}

interface ImportCompleteSummaryProps {
  newPatientsCount: number;
  matchedPatientsCount: number;
  appointmentsCount: number;
  failedCount: number;
  errors?: ImportErrorDetail[];
  sessionId?: string;
}

// =============================================================================
// Helpers
// =============================================================================

function buildSummaryLines(
  newPatientsCount: number,
  matchedPatientsCount: number,
  appointmentsCount: number,
): string[] {
  const lines: string[] = [];
  if (newPatientsCount > 0) {
    lines.push(`${newPatientsCount} new patient${newPatientsCount === 1 ? '' : 's'} created`);
  }
  if (matchedPatientsCount > 0) {
    lines.push(`${matchedPatientsCount} existing patient${matchedPatientsCount === 1 ? '' : 's'} updated`);
  }
  if (appointmentsCount > 0) {
    lines.push(`${appointmentsCount} appointment${appointmentsCount === 1 ? '' : 's'} scheduled`);
  }
  return lines;
}

// =============================================================================
// Component
// =============================================================================

export const ImportCompleteSummary = ({
  newPatientsCount,
  matchedPatientsCount,
  appointmentsCount,
  failedCount,
  errors = [],
  sessionId,
}: ImportCompleteSummaryProps) => {
  const navigate = useNavigate();
  const hasFailures = failedCount > 0;
  const [copied, setCopied] = useState(false);

  const summaryLines = buildSummaryLines(newPatientsCount, matchedPatientsCount, appointmentsCount);

  const formatErrorsForClipboard = useCallback(() => {
    const lines = [
      `Import Session: ${sessionId ?? 'N/A'}`,
      `Result: ${summaryLines.join(', ')}${hasFailures ? `, ${failedCount} failed` : ''}`,
      '',
      ...errors.map((err, i) =>
        [
          `Error ${i + 1}:`,
          `  Message: ${err.message}`,
          err.errorType ? `  Type: ${err.errorType}` : null,
          err.timestamp ? `  Time: ${err.timestamp}` : null,
        ]
          .filter(Boolean)
          .join('\n'),
      ),
    ];
    return lines.join('\n');
  }, [errors, summaryLines, failedCount, hasFailures, sessionId]);

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(formatErrorsForClipboard());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [formatErrorsForClipboard]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
        py: 6,
      }}
    >
      {hasFailures ? (
        <WarningIcon sx={{ fontSize: 48, color: 'warning.main' }} />
      ) : (
        <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
      )}

      <Typography variant="h5" sx={{ fontWeight: 600 }}>
        Import Complete
      </Typography>

      {/* Breakdown lines */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
        {summaryLines.map((line) => (
          <Typography key={line} variant="body1" color="text.secondary">
            {line}
          </Typography>
        ))}
        {hasFailures && (
          <Typography variant="body1" color="error.main" sx={{ fontWeight: 600 }}>
            {failedCount} failed
          </Typography>
        )}
      </Box>

      {/* Expandable error details */}
      {hasFailures && errors.length > 0 && (
        <Box sx={{ width: '100%', maxWidth: 600, mt: 1 }}>
          <Accordion
            disableGutters
            elevation={0}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ minHeight: 44 }}
            >
              <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
                {errors.length} error{errors.length === 1 ? '' : 's'}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mb: 1,
                }}
              >
                <Tooltip title={copied ? 'Copied!' : 'Copy error details'}>
                  <IconButton size="small" onClick={handleCopy}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {errors.map((err, idx) => (
                  <Paper
                    key={idx}
                    variant="outlined"
                    sx={{ px: 1.5, py: 1 }}
                  >
                    <Typography variant="body2">
                      {err.message}
                    </Typography>
                    {err.errorType && (
                      <Typography variant="caption" color="text.secondary">
                        {err.errorType}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      <Button
        variant="contained"
        onClick={() => navigate('/patients')}
        sx={{ mt: 1 }}
      >
        Go to Patients
      </Button>
    </Box>
  );
};
